import { Component, Element, Event, EventEmitter, h, Host, Prop } from '@stencil/core';

/**
 * Component that expand to show additional content
 *
 * @part content - The shadow part to target to content HTML div element.
 * @part content-wrap - The shadow part to target to content HTML div element.
 * @part button - The shadow part to target to HTML button element.
 * @part icon - The shadow part to target to HTMLPkIcon element inside the button.
 *
 * @slot content - The slot for the content to show on expand.
 */
@Component({
  tag: 'pk-collapse',
  styleUrl: './scss/pk-collapse.scss',
  shadow: true,
})
export class PkCollapse {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkCollapseElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** Set the horizontal position alignment of the button trigger */
  @Prop({ reflect: true }) position: 'center' | 'left' | 'right' = 'left';

  /** Name of the icon to render beside the label text.
   * Please make sure to choose an icon that can be rotated! */
  @Prop({ reflect: true }) iconName: string = 'navigation-chevron-down';

  /** Label when the content is expanded */
  @Prop({ reflect: true }) expandedLabel!: string;

  /** Label when the content is collapsed */
  @Prop({ reflect: true }) collapsedLabel!: string;

  /** If true, the content will be expanded when the component is loaded */
  @Prop({ reflect: true, mutable: true }) expand: boolean = false;

  // Prop lifecycle events
  // =======================

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Handler to be called on button click */
  @Event() pkChange: EventEmitter<{ expanded: boolean }>;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private handleButtonClick = () => {
    this.expand = !this.expand;
    this.pkChange.emit({ expanded: this.expand });
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <Host>
        <div
          class={{
            collapse__content: true,
            expand: this.expand,
          }}
          part="content"
        >
          <div class="collapse__content-wrap overflow-hidden" part="content-wrap">
            <slot name="content" />
          </div>
        </div>
        <pk-button
          appearance="text"
          class={{ collapse__button: true, [`align-${this.position}`]: true }}
          onPkClick={this.handleButtonClick}
          type="button"
          exportparts="button"
        >
          {this.expand ? this.expandedLabel : this.collapsedLabel}
          <pk-icon class={{ 'collapse__button--icon': true, expand: this.expand }} name={this.iconName} part="icon" />
        </pk-button>
      </Host>
    );
  }
}
